/**
 * The Review contract — one flat router, `reviewContract` (`/reviews`). See the Performance Reviews
 * plan's "Contract & API" section for the full design this file implements; `performanceReviewSummarySchema`
 * is exported for `review-cycle.contract.ts`'s `listCycleReviews` to reuse (a one-way import — this
 * file never imports back from `review-cycle.contract.ts`, keeping the three routers independent).
 *
 * THE KEY DESIGN DECISION THIS FILE ENCODES: the mockup's 4-stage UI tracker (Goal Evaluation ->
 * Competency -> 360° Peer -> Manager Review) is NOT 4 workflow steps. `stage`/`status` advance via
 * `submitSelfAssessment` (own work, no `@hrms/workflow` involvement) through stages 1-3; only
 * `submitReview` starts a `WorkflowInstance` (`entityType: 'performance_review'`), gated 412 until
 * enough `FeedbackRequest` responses exist (Section 3, no new model — see the plan's verified
 * finding on why runtime-named peers cannot be workflow steps). `approveReview`/`rejectReview` are
 * the only two endpoints that call the engine's `decide()`.
 *
 * `getReview`'S `peerResponses` FIELD IS WHAT `submitReview`'S 412 READS — `{submitted, requested,
 * required}` lets the UI render "1 of 3 Approved" without a second round trip to Section 3's
 * contract.
 *
 * 360° PEER FEEDBACK ADDS NO MODEL HERE — it reuses `feedbackContract`'s `FeedbackRequest`/
 * `Feedback` rows directly (with `reviewId` set), per the plan's verified conclusion. There is no
 * "peer feedback" sub-resource under `/reviews` for that reason; the peer panel is read through
 * `feedbackContract` with a `reviewId` filter.
 *
 * `draftJustification`'S INPUT SCHEMA IS THE PII BOUNDARY (invariant 9's tool-permission story
 * applies at the contract layer too) — no `personId`, email, grade, salary, or DOB field is
 * accepted; the route resolves competency/goal/check-in context server-side from the path's
 * `reviewId`/`competencyId`, never from the body. See `@hrms/ai`'s
 * `performance.draft_self_justification` tool for the full PII-boundary reasoning.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  REVIEW_EVIDENCE_KINDS,
  REVIEW_STAGES,
  REVIEW_STATUSES,
  REVIEW_QUESTION_TYPES,
  REVIEW_QUESTION_REVIEWER_TYPES,
} from '../enums.js';
import {
  dateOnlySchema,
  errorBodySchema,
  instantSchema,
  orgIdSchema,
  paginatedSchema,
  paginationQuerySchema,
  percentSchema,
  ratingValueSchema,
  uuidSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

// ---------------------------------------------------------------------------
// Shared enum schemas
// ---------------------------------------------------------------------------

export const reviewStageSchema = z.enum(REVIEW_STAGES);
export const reviewStatusSchema = z.enum(REVIEW_STATUSES);
export const reviewEvidenceKindSchema = z.enum(REVIEW_EVIDENCE_KINDS);
export const reviewOutcomeSchema = z.enum(['confirmed', 'extended', 'terminated', 'n/a']);

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

export const reviewCompetencyRatingSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  reviewId: uuidSchema,
  competencyId: uuidSchema,
  competencyName: z.string(),
  /** Snapshotted from the template at review creation — a template edited mid-cycle must not
   * retroactively reweight a submitted review. See the plan's Data model section. */
  weightPercent: percentSchema,
  expectedLevel: z.number().int().nullable(),
  selfRating: ratingValueSchema.nullable(),
  managerRating: ratingValueSchema.nullable(),
  finalRating: ratingValueSchema.nullable(),
  selfJustification: z.string().nullable(),
  managerComment: z.string().nullable(),
});
export type ReviewCompetencyRating = z.infer<typeof reviewCompetencyRatingSchema>;

export const reviewEvidenceSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  reviewId: uuidSchema,
  ratingId: uuidSchema.nullable(),
  kind: reviewEvidenceKindSchema,
  label: z.string(),
  url: z.string().nullable(),
  goalId: uuidSchema.nullable(),
  attachmentId: uuidSchema.nullable(),
  createdAt: instantSchema,
});
export type ReviewEvidence = z.infer<typeof reviewEvidenceSchema>;

export const reviewGoalLinkSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  reviewId: uuidSchema,
  goalId: uuidSchema,
  goalTitle: z.string(),
  /** Snapshotted, not read live from `Goal` — a goal that keeps moving after the cycle closed must
   * not change a signed-off score. See the plan's Data model section. */
  weightPercent: percentSchema,
  /** Nullable until the goal-evaluation step computes it — see `GoalAchievementPanel.tsx`'s
   * exclusion math for how the UI treats a not-yet-evaluated link. */
  achievedPercent: percentSchema.nullable(),
  managerAdjustedPercent: percentSchema.nullable(),
  managerComment: z.string().nullable(),
});
export type ReviewGoalLink = z.infer<typeof reviewGoalLinkSchema>;

// ---------------------------------------------------------------------------
// Custom review questions (PRF-8) — built on @hrms/custom-fields' generic shape
// ---------------------------------------------------------------------------

export const reviewQuestionTypeSchema = z.enum(REVIEW_QUESTION_TYPES);
export const reviewQuestionReviewerTypeSchema = z.enum(REVIEW_QUESTION_REVIEWER_TYPES);

export const reviewQuestionOptionSchema = z.object({
  value: z.string().min(1).max(100),
  label: z.string().min(1).max(200),
  sortOrder: z.number().int(),
});
export type ReviewQuestionOption = z.infer<typeof reviewQuestionOptionSchema>;

/** One answer to one template question, on one review — `type`/`label`/`options`/`weightPercent`
 * are snapshotted at first-answer time (see the model's own comment in schema.prisma), never read
 * live from the template. `value`'s shape depends on `type`: a number string for `rating`, a
 * string for `text`/`single_choice`, a string array for `multiple_choice` — validated with
 * `@hrms/custom-fields`' `validateCustomFieldValue` against these same snapshot columns. */
export const reviewQuestionAnswerSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  reviewId: uuidSchema,
  questionId: uuidSchema,
  reviewerType: reviewQuestionReviewerTypeSchema,
  type: reviewQuestionTypeSchema,
  label: z.string(),
  options: z.array(reviewQuestionOptionSchema).nullable(),
  weightPercent: percentSchema.nullable(),
  value: z.union([z.string(), z.array(z.string())]).nullable(),
  answeredBy: uuidSchema.nullable(),
  answeredAt: instantSchema.nullable(),
});
export type ReviewQuestionAnswer = z.infer<typeof reviewQuestionAnswerSchema>;

export const answerQuestionBodySchema = z.object({
  value: z.union([z.string().max(4000), z.array(z.string().max(100)).max(20)]),
});
export type AnswerQuestionBody = z.infer<typeof answerQuestionBodySchema>;

/** Gates `submitReview`'s 412 — see this file's header. */
export const peerResponsesSchema = z.object({
  submitted: z.number().int().nonnegative(),
  requested: z.number().int().nonnegative(),
  required: z.number().int().nonnegative(),
});
export type PeerResponses = z.infer<typeof peerResponsesSchema>;

/**
 * The central record. `managerPersonId`/`departmentId` are snapshots at creation, not derived live
 * — see the plan's Data model section on why this deliberately diverges from `resolvePersonScope`
 * (ownership vs. authorisation). `stage`/`status` are separate columns and `stage` is mirrored FROM
 * the workflow, never authored independently.
 */
export const performanceReviewSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  cycleId: uuidSchema,
  cycleLabel: z.string(),
  templateId: uuidSchema.nullable(),
  personId: uuidSchema,
  personName: z.string(),
  managerPersonId: uuidSchema.nullable(),
  departmentId: uuidSchema.nullable(),
  evaluationLevel: z.string().nullable(),
  stage: reviewStageSchema,
  status: reviewStatusSchema,
  workflowInstanceId: uuidSchema.nullable(),
  selfOverallComment: z.string().nullable(),
  /** The employee's OWN score from their self-assessment (stored at `submitSelfAssessment`) —
   * display only, never scored (D1). Always visible to the employee. */
  selfOverallScore: z.string().nullable(),
  goalScore: z.string().nullable(),
  competencyScore: z.string().nullable(),
  overallScore: z.string().nullable(),
  /** Calibration output — stays a separate column from `overallScore` forever. See the plan's
   * Requirements section: collapsing them destroys the evidence a human overrode the math. */
  finalRating: ratingValueSchema.nullable(),
  /** Manager+HR only — never shown to the reviewee, before or after release (decision Q4). */
  potentialRating: z.enum(['low', 'medium', 'high']).nullable().optional(),
  potentialComment: z.string().nullable(),
  outcome: reviewOutcomeSchema.nullable(),
  outcomeEffectiveOn: dateOnlySchema.nullable(),
  agreed: z.boolean().nullable(),
  employeeComment: z.string().nullable(),
  /** When HR released this review's results to the employee (cycle-wide `releaseResults`, or a
   * per-review `releaseReview` after an approved appeal). Null before release.
   *
   * VISIBILITY IS DECIDED ON THE SERVER (ADR 0031 §6). Until the review's results are released the
   * reviewee receives null for every manager-side field — `goalScore`/`competencyScore`/
   * `overallScore`/`finalRating`/`outcome`, each rating's `managerRating`/`managerComment`/
   * `finalRating`, each goal link's `managerAdjustedPercent`/`managerComment` — and no manager
   * question answers. `potentialRating`/`potentialComment` are null for the reviewee always. Every
   * one of those is nullable here, so clients must render null as "not released yet", never as
   * zero. Managers and HR with `review:read` over the reviewee see everything. */
  releasedAt: instantSchema.nullable(),
  /** Set when an approved appeal reopened the review for its manager (T2.11, plan §5H): status back
   * to `submitted`, stage `manager_review`. `releasedAt` is kept; the review's results are hidden
   * from the reviewee again until HR re-releases it (`releaseReview`). Stays set afterwards as the
   * record that the result was revised. Optional on the wire for clients built before it existed. */
  reopenedAt: instantSchema.nullable().optional(),
  createdAt: instantSchema,
});
export type PerformanceReview = z.infer<typeof performanceReviewSchema>;

/** The lightweight row `listCycleReviews`/`listReviews` return — one per roster row, deliberately
 * lighter than `performanceReviewSchema` (no ratings/goalLinks/evidence/peerResponses), same
 * "summary vs. full detail" split as `goalSummarySchema`/`goalSchema`. */
export const performanceReviewSummarySchema = z.object({
  id: uuidSchema,
  cycleId: uuidSchema,
  cycleLabel: z.string(),
  personId: uuidSchema,
  personName: z.string(),
  managerPersonId: uuidSchema.nullable(),
  departmentId: uuidSchema.nullable(),
  stage: reviewStageSchema,
  status: reviewStatusSchema,
  overallScore: z.string().nullable(),
  finalRating: ratingValueSchema.nullable(),
  createdAt: instantSchema,
  /** From the cycle's template/config — lets a summary row render a scale-correct ring/badge
   * without a second round trip to fetch the cycle. */
  ratingScaleMax: z.number().int(),
});
export type PerformanceReviewSummary = z.infer<typeof performanceReviewSummarySchema>;

// ---------------------------------------------------------------------------
// Review appeals (PRF-10)
// ---------------------------------------------------------------------------

/** `open`: the row exists but the `review_appeal` WorkflowInstance has not attached yet (the same
 * brief optimistic-write window `submitReview`'s `stage: 'manager_review'` has before its own
 * `startWorkflow` call resolves — see review.ts). `in_review`: the workflow is attached and HR's
 * step is active. `resolved`: `decideAppeal` recorded a decision. */
export const reviewAppealStatusSchema = z.enum(['open', 'in_review', 'resolved']);
export const reviewAppealDecisionSchema = z.enum(['approved', 'rejected']);

/** One `ReviewAppeal` row. Reused both for the HR queue (`listAppeals`) and embedded as
 * `latestAppeal` on `getReview`'s response — the mild redundancy of `cycleLabel`/`personName` when
 * embedded (already available elsewhere on the review) is cheaper than a second, near-identical
 * schema. */
export const reviewAppealSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  reviewId: uuidSchema,
  personId: uuidSchema,
  personName: z.string(),
  managerPersonId: uuidSchema.nullable(),
  cycleLabel: z.string(),
  reviewOverallScore: z.string().nullable(),
  /** The appealed review's current status — lets HR's queue tell "upheld, manager re-assessing"
   * (`submitted`) from "re-assessed, ready to re-release" (`manager_reviewed`) without opening it.
   * Optional on the wire for clients built before it existed. */
  reviewStatus: reviewStatusSchema.optional(),
  employeeComment: z.string(),
  status: reviewAppealStatusSchema,
  decision: reviewAppealDecisionSchema.nullable(),
  resolutionComment: z.string().nullable(),
  resolvedByPersonId: uuidSchema.nullable(),
  resolvedAt: instantSchema.nullable(),
  reopenedAt: instantSchema.nullable(),
  reopenComment: z.string().nullable(),
  workflowInstanceId: uuidSchema.nullable(),
  createdAt: instantSchema,
});
export type ReviewAppeal = z.infer<typeof reviewAppealSchema>;

/** A worklist across every cycle, not scoped to one — an HR appeal queue is an ongoing to-do list,
 * unlike `listCycleReviews`'s per-cycle roster. */
export const listAppealsQuerySchema = paginationQuerySchema.extend({
  status: reviewAppealStatusSchema.optional(),
});

/** "HR resolves WITH comment" (the plan's own wording) — unlike `decideReviewBodySchema`'s optional
 * one, `comment` here is required: the employee reads this to understand why the appeal was upheld
 * or dismissed. */
export const decideAppealBodySchema = z.object({
  decision: reviewAppealDecisionSchema,
  comment: z.string().trim().min(1).max(2000),
});
export type DecideAppealBody = z.infer<typeof decideAppealBodySchema>;

/** Reopening is rare and consequential enough (reversing a resolved HR decision) that its own
 * comment is also required, same reasoning as `decideAppealBodySchema`'s. */
export const reopenAppealBodySchema = z.object({
  comment: z.string().trim().min(1).max(2000),
});
export type ReopenAppealBody = z.infer<typeof reopenAppealBodySchema>;

/** `getReview`'s full response — the review plus every child collection in one round trip. */
export const reviewDetailSchema = performanceReviewSchema.extend({
  ratings: z.array(reviewCompetencyRatingSchema),
  goalLinks: z.array(reviewGoalLinkSchema),
  evidence: z.array(reviewEvidenceSchema),
  peerResponses: peerResponsesSchema,
  questionAnswers: z.array(reviewQuestionAnswerSchema),
  /** The most recent appeal on this review, if any — "the employee sees status" (PRF-10). A review
   * can accumulate more than one over time (HR reopens, a second disagreement follows), but the UI
   * only ever needs the latest. */
  latestAppeal: reviewAppealSchema.nullable(),
});
export type ReviewDetail = z.infer<typeof reviewDetailSchema>;
export const performanceReviewDetailSchema = reviewDetailSchema;
export type PerformanceReviewDetail = ReviewDetail;

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

export const listReviewsQuerySchema = paginationQuerySchema.extend({
  cycleId: uuidSchema.optional(),
  /** Defaults to the caller's own reviews. */
  personId: uuidSchema.optional(),
  managerPersonId: uuidSchema.optional(),
  stage: reviewStageSchema.optional(),
  status: reviewStatusSchema.optional(),
});

/** HR/admin ad-hoc creation of a single review — bulk creation goes through
 * `reviewCycleContract`'s `assignReviews` instead. */
export const createReviewBodySchema = z.object({
  cycleId: uuidSchema,
  personId: uuidSchema,
  templateId: uuidSchema.optional(),
  managerPersonId: uuidSchema.optional(),
  departmentId: uuidSchema.optional(),
});

export const upsertSelfRatingBodySchema = z.object({
  rating: ratingValueSchema,
  justification: z.string().trim().max(4000).optional(),
});

/** `review.manager:submit`-gated — never `review:submit`, see this file's header and the plan's
 * gaming-vector note. */
export const upsertManagerRatingBodySchema = z.object({
  rating: ratingValueSchema,
  comment: z.string().trim().max(2000).optional(),
});

export const updateSelfSummaryBodySchema = z.object({
  selfOverallComment: z.string().trim().max(4000),
});

export const linkGoalsBodySchema = z.object({
  goals: z.array(z.object({ goalId: uuidSchema, weightPercent: percentSchema })).max(20),
});

export const updateGoalLinkBodySchema = z.object({
  managerAdjustedPercent: percentSchema.optional(),
  managerComment: z.string().trim().max(2000).optional(),
});

export const addEvidenceBodySchema = z.object({
  ratingId: uuidSchema.optional(),
  kind: reviewEvidenceKindSchema,
  label: z.string().trim().min(1).max(200),
  url: z.string().trim().max(2000).optional(),
  goalId: uuidSchema.optional(),
  attachmentId: uuidSchema.optional(),
});

export const submitSelfAssessmentBodySchema = z.object({});

export const submitReviewBodySchema = z.object({});

export const decideReviewBodySchema = z.object({
  comment: z.string().trim().max(2000).optional(),
  potentialRating: z.enum(['low', 'medium', 'high']).optional(),
});

/** Gains `agreed`/`employeeComment` over a plain acknowledge — the plan's appeal gap-analysis item:
 * a disagreement emits an outbox event routing to HR instead of silently closing. */
export const acknowledgeReviewBodySchema = z.object({
  agreed: z.boolean(),
  employeeComment: z.string().trim().max(2000).optional(),
});

/** The manager of record completes their assessment (manager-led scoring, D1): the server computes
 * `overallScore`/`goalScore`/`competencyScore` from the manager's ratings and starts the review
 * workflow's conditional HR outlier step. The score inputs are already on the review; the body
 * carries only the manager's 9-box POTENTIAL assessment (the other axis of the talent grid, never
 * part of the score). Both fields are optional: omitted leaves any earlier value as it is.
 * Manager + HR only — never shown to the reviewee (decision Q4). */
export const submitManagerAssessmentBodySchema = z.object({
  potentialRating: z.enum(['low', 'medium', 'high']).optional(),
  potentialComment: z.string().trim().max(2000).optional(),
});
export type SubmitManagerAssessmentBody = z.infer<typeof submitManagerAssessmentBodySchema>;

/** Send a review in `manager_review` back to the employee (stage `competency_assessment`, scores
 * cleared). The comment is required: the employee reads it to know what to revise. */
export const returnToEmployeeBodySchema = z.object({
  comment: z.string().trim().min(1).max(2000),
});
export type ReturnToEmployeeBody = z.infer<typeof returnToEmployeeBodySchema>;

/** HR withdraws a review before results are released (a leaver, a wrong assignment). Cancelled
 * reviews are excluded from release, rollups, merit and insights; the reason is audited. */
export const cancelReviewBodySchema = z.object({
  reason: z.string().trim().min(1).max(2000),
});
export type CancelReviewBody = z.infer<typeof cancelReviewBodySchema>;

/** Re-release ONE review after an approved appeal reopened and re-scored it (plan §5H, R21).
 * Legal only for a review an approved appeal reopened (`reopenedAt` set) that is `manager_reviewed`
 * again, in a `released` cycle — 409 otherwise. `finalRating` becomes the new effective score (the
 * reopen cleared the old one), and the employee's acknowledgement (`agreed`/`employeeComment`) is
 * reset so they acknowledge — or appeal — the revised result; prior values stay in the audit log. */
export const releaseReviewBodySchema = z.object({});

/** Input to the AI drafting tool — mirrors `draftCheckInBodySchema`'s PII-boundary shape exactly.
 * Everything person-identifying is resolved server-side from the path, never accepted here. */
export const draftJustificationBodySchema = z.object({
  tone: z.enum(['concise', 'formal', 'encouraging']).optional(),
  maxWords: z.number().int().min(20).max(400).optional(),
  locale: z.string().max(10).optional(),
});

export const draftJustificationResponseSchema = z.object({
  draftJustification: z.string(),
});

/** Admin roster action (`review:manage`) — reassign the manager of record on one review. Does not
 * touch `Employment`/`resolvePersonScope`'s live reporting line, only this review's snapshot (see
 * `performanceReviewSchema`'s header on why the two deliberately diverge). Only before results are
 * released (409 after); the new manager must be a current employee other than the reviewee (422).
 * Re-owns the open manager-side work: a pending review workflow instance is superseded and its
 * scores cleared so the new manager re-submits; the new manager is notified (T4.3, #16). */
export const reassignManagerBodySchema = z.object({
  managerPersonId: uuidSchema,
});

/** Admin roster action (`review:manage`) — resend the same reminder `notify()` would send on
 * assignment, for a person who has not started/submitted. Goes through `@hrms/notify` (invariant
 * 6), never a hand-rolled send; no new model — nudges aren't stored, only fanned out. */
export const nudgeReviewResponseSchema = z.object({
  sent: z.boolean(),
});

export const exportReviewPdfQuerySchema = z.object({
  format: z.enum(['pdf', 'excel']).default('pdf'),
});

export const exportReviewPdfResponseSchema = z.object({
  /** A short-lived presigned URL, same convention as `Attachment`'s access pattern — never a public
   * bucket. */
  url: z.string(),
  expiresAt: instantSchema,
});

// ---------------------------------------------------------------------------
// Contract — /reviews
// ---------------------------------------------------------------------------

export const reviewContract = c.router(
  {
    listReviews: {
      method: 'GET',
      path: '/',
      query: listReviewsQuerySchema,
      responses: {
        200: paginatedSchema(performanceReviewSummarySchema),
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "List reviews in the caller's visible scope",
      metadata: routeMeta('review:read'),
    },

    // PRF-10: a literal path (`/appeals`), same one-segment shape as `getReview`'s `/:reviewId`
    // right below — `tsRestRouter` (apps/api/src/lib/ts-rest.ts) registers routes on an Express
    // Router in exactly this object's key order, and Express matches path segments in registration
    // order. This MUST stay before `getReview` in this object, or `GET /reviews/appeals` silently
    // matches `getReview` instead (`reviewId: 'appeals'`) and 404s. Do not reorder without moving
    // this comment's warning with it.
    listAppeals: {
      method: 'GET',
      path: '/appeals',
      query: listAppealsQuerySchema,
      responses: {
        200: paginatedSchema(reviewAppealSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: "HR's appeal queue, across every cycle — review:appeal:read",
      metadata: routeMeta('review:appeal:read'),
    },

    getReview: {
      method: 'GET',
      path: '/:reviewId',
      responses: {
        200: reviewDetailSchema,
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Get one review — ratings, goal links, evidence, and the peer-response gate count',
      metadata: routeMeta('review:read'),
    },

    createReview: {
      method: 'POST',
      path: '/',
      body: createReviewBodySchema,
      responses: {
        201: performanceReviewSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Create a single review ad hoc — review:manage',
      metadata: routeMeta('review:manage'),
    },

    upsertSelfRating: {
      method: 'PUT',
      path: '/:reviewId/competencies/:competencyId/self-rating',
      body: upsertSelfRatingBodySchema,
      responses: {
        200: reviewCompetencyRatingSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: "Set the caller's own self-rating for one competency — review:submit, self scope",
      metadata: routeMeta('review:submit'),
    },

    upsertManagerRating: {
      method: 'PUT',
      path: '/:reviewId/competencies/:competencyId/manager-rating',
      body: upsertManagerRatingBodySchema,
      responses: {
        200: reviewCompetencyRatingSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        "Set the manager's rating for one competency — review.manager:submit (NOT review:submit — see this file's header)",
      metadata: routeMeta('review.manager:submit'),
    },

    updateSelfSummary: {
      method: 'PUT',
      path: '/:reviewId/self-summary',
      body: updateSelfSummaryBodySchema,
      responses: {
        200: performanceReviewSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "Save the review's free-text self-summary — review:submit, self scope",
      metadata: routeMeta('review:submit'),
    },

    linkGoals: {
      method: 'PUT',
      path: '/:reviewId/goal-links',
      body: linkGoalsBodySchema,
      responses: {
        200: paginatedSchema(reviewGoalLinkSchema),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary: "Replace the review's linked-goal set (the Goal Evaluation step) — review:submit",
      metadata: routeMeta('review:submit'),
    },

    updateGoalLink: {
      method: 'PATCH',
      path: '/:reviewId/goal-links/:goalLinkId',
      body: updateGoalLinkBodySchema,
      responses: {
        200: reviewGoalLinkSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary: "Manager adjusts a linked goal's achieved percent — review.manager:submit",
      metadata: routeMeta('review.manager:submit'),
    },

    /** One route for both self and manager answers, per this file's header on that boundary — the
     * caller's actual relationship to the review (subject vs. managerPersonId) decides which,
     * server-side, never a client-supplied reviewerType. review:submit covers a self answer;
     * review.manager:submit covers a manager one; the route accepts either at the gate and the
     * handler enforces the specific one for the specific answer. */
    answerQuestion: {
      method: 'PUT',
      path: '/:reviewId/questions/:questionId/answer',
      body: answerQuestionBodySchema,
      responses: {
        200: reviewQuestionAnswerSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        "Answer one custom review question as self or manager (whichever the caller actually is) — review:submit or review.manager:submit",
      metadata: routeMeta(['review:submit', 'review.manager:submit']),
    },

    addEvidence: {
      method: 'POST',
      path: '/:reviewId/evidence',
      body: addEvidenceBodySchema,
      responses: {
        201: reviewEvidenceSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Attach an evidence chip (link, document, or goal) — review:submit',
      metadata: routeMeta('review:submit'),
    },

    removeEvidence: {
      method: 'DELETE',
      path: '/:reviewId/evidence/:evidenceId',
      body: z.object({}),
      responses: {
        200: z.object({ id: uuidSchema }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Remove an evidence chip — review:submit',
      metadata: routeMeta('review:submit'),
    },

    submitSelfAssessment: {
      method: 'POST',
      path: '/:reviewId/submit-self-assessment',
      body: submitSelfAssessmentBodySchema,
      responses: {
        200: performanceReviewSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        "Advance stage goal_evaluation/competency_assessment -> peer_feedback and recompute scores — no workflow involvement, see this file's header",
      metadata: routeMeta('review:submit'),
    },

    submitReview: {
      method: 'POST',
      path: '/:reviewId/submit',
      body: submitReviewBodySchema,
      responses: {
        200: performanceReviewSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
      },
      summary:
        'Starts the manager-review WorkflowInstance — 412 when stage !== peer_feedback, or when submitted peer responses are below cycle.minPeerResponses (the message names the actual count)',
      metadata: routeMeta(null),
    },

    approveReview: {
      method: 'POST',
      path: '/:reviewId/approve',
      body: decideReviewBodySchema,
      responses: {
        200: performanceReviewSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
      },
      summary: 'Approve the step currently awaiting the caller',
      metadata: routeMeta(null),
    },

    rejectReview: {
      method: 'POST',
      path: '/:reviewId/reject',
      body: decideReviewBodySchema,
      responses: {
        200: performanceReviewSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
      },
      summary: 'Reject the step currently awaiting the caller',
      metadata: routeMeta(null),
    },

    acknowledgeReview: {
      method: 'POST',
      path: '/:reviewId/acknowledge',
      body: acknowledgeReviewBodySchema,
      responses: {
        200: performanceReviewSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary:
        'The employee acknowledges their RELEASED review (409 before release) — agreed:true -> acknowledged; agreed:false opens a ReviewAppeal (PRF-10) and the review becomes appealed',
      metadata: routeMeta('review:submit'),
    },

    decideAppeal: {
      method: 'POST',
      path: '/appeals/:appealId/decide',
      body: decideAppealBodySchema,
      responses: {
        200: reviewAppealSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
      },
      summary:
        "HR's decision on an appeal's review_appeal WorkflowInstance. review:appeal:approve to approve (upheld), review:appeal:reject to reject (dismissed) — never a second approval mechanism (see merit.contract.ts's decideCycleBudget, the same split). On the workflow's terminal outcome only: dismissed → the review is back to released (the employee may acknowledge); upheld → the review is reopened for its manager (status submitted, stage manager_review, finalRating cleared) and HR re-releases it with releaseReview once re-assessed",
      metadata: routeMeta(['review:appeal:approve', 'review:appeal:reject']),
    },

    reopenAppeal: {
      method: 'POST',
      path: '/appeals/:appealId/reopen',
      body: reopenAppealBodySchema,
      responses: {
        200: reviewAppealSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary:
        'Reopen a resolved appeal for a fresh decision — review:appeal:manage. The review goes back to appealed; 409 unless the review is released (not while it is reopened for the manager, acknowledged or closed)',
      metadata: routeMeta('review:appeal:manage'),
    },

    reassignManager: {
      method: 'PATCH',
      path: '/:reviewId/manager',
      body: reassignManagerBodySchema,
      responses: {
        200: performanceReviewSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: "Reassign a review's manager of record — review:manage, roster admin action",
      metadata: routeMeta('review:manage'),
    },

    nudgeReview: {
      method: 'POST',
      path: '/:reviewId/nudge',
      body: z.object({}),
      responses: {
        200: nudgeReviewResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary:
        "Resend a reminder to the review's current stage owner via @hrms/notify — review:manage, roster admin action",
      metadata: routeMeta('review:manage'),
    },

    draftJustification: {
      method: 'POST',
      path: '/:reviewId/competencies/:competencyId/draft-justification',
      body: draftJustificationBodySchema,
      responses: {
        200: draftJustificationResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
        503: errorBodySchema,
      },
      summary:
        'AI-drafted self-justification suggestion — ai.copilot:use AND review:submit, read-only, persists nothing; 422 on a blocking DLP finding, 503 when no provider is configured',
      metadata: routeMeta(['ai.copilot:use', 'review:read', 'review:submit']),
    },

    exportReviewPdf: {
      method: 'GET',
      path: '/:reviewId/export',
      query: exportReviewPdfQuerySchema,
      responses: {
        200: exportReviewPdfResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        503: errorBodySchema,
      },
      summary: 'A short-lived presigned URL to a generated PDF/Excel appraisal summary',
      metadata: routeMeta('review:read'),
    },

    // --- Review lifecycle (performance remediation §5B/§5H/§6). Illegal transitions are 409. ---

    submitManagerAssessment: {
      method: 'POST',
      path: '/:reviewId/manager-assessment/submit',
      body: submitManagerAssessmentBodySchema,
      responses: {
        200: performanceReviewSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
      },
      summary:
        'The manager of record submits their assessment (with an optional 9-box potential rating): computes the manager-led score and starts the HR outlier check — 412 while a template competency lacks a manager rating, 409 outside manager_review. Also the re-submission after an approved appeal reopened the review. Requires Idempotency-Key',
      metadata: routeMeta('review.manager:submit'),
    },

    returnToEmployee: {
      method: 'POST',
      path: '/:reviewId/return',
      body: returnToEmployeeBodySchema,
      responses: {
        200: performanceReviewSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        /** The review is not at stage manager_review. */
        412: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        'Return a review in manager_review to the employee for revision (stage competency_assessment, scores cleared) — the manager of record or HR; 412 from any other stage. Requires Idempotency-Key',
      metadata: routeMeta(['review.manager:submit', 'review:manage']),
    },

    cancelReview: {
      method: 'POST',
      path: '/:reviewId/cancel',
      body: cancelReviewBodySchema,
      responses: {
        200: performanceReviewSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        'Cancel a review before results are released (supersedes a pending workflow; excluded from release, rollups and merit; the reason is audited and the employee notified) — review:manage, 409 once released. Requires Idempotency-Key',
      metadata: routeMeta('review:manage'),
    },

    releaseReview: {
      method: 'POST',
      path: '/:reviewId/release',
      body: releaseReviewBodySchema,
      responses: {
        200: performanceReviewSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary:
        'Re-release one review after an approved appeal reopened and re-scored it (manager_reviewed again, cycle released — 409 otherwise); finalRating = the new effective score, the acknowledgement is reset and the employee notified — review.cycle:manage. Requires Idempotency-Key',
      metadata: routeMeta('review.cycle:manage'),
    },
  },
  { pathPrefix: '/reviews' },
);
